import { makeStyles, Popper, useMediaQuery, useTheme, ClickAwayListener } from '@material-ui/core'
import { getPipelines } from '@paintscout/util/builder/options/get-pipelines/get-pipelines'
import { useFlags } from 'shared/components/common/FlagsProvider'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import ExpandLessIcon from '@material-ui/icons/ExpandLess'
import React, { useEffect, useRef, useState } from 'react'
import type { DealDocument, Pipeline, PipelineStage } from 'paintscout'
import type { Theme } from '@material-ui/core/styles'
import type { StyleClasses } from '../../styles'
import Typography from '../Typography'
import classNames from 'classnames'
import Button from '../Button'
import Paper from '../Paper'
import { useIsMobile } from '../../hooks'
import { useClientOptions } from '../../providers'
import type { Deal } from '@paintscout/api'

const useStyles = makeStyles<Theme, { disabled: boolean; quoteView: boolean }>(
  (theme) => ({
    root: {},
    container: (props) => ({
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1),
      marginTop: props.quoteView ? 0 : `-${theme.spacing(3.5)}px`
    }),
    textContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(0.5),
      color: theme.palette.grey[600],
      '&:hover': {
        cursor: 'pointer',
        color: theme.palette.grey[800]
      }
    },
    popper: {
      zIndex: 1400,
      boxShadow: theme.shadows[4],
      marginTop: '1px'
    },
    paper: {
      backgroundColor: theme.palette.background.paper,
      boxShadow: 'none',
      minWidth: '250px'
    },
    pipelineContainer: {
      width: '100%',
      display: 'flex',
      gap: theme.spacing(0.5),
      [theme.breakpoints.down('sm')]: {
        gap: theme.spacing(1)
        // marginBottom: theme.spacing(2)
      },
      '@media print': {
        display: 'none'
      }
    },
    stageContainer: {
      position: 'relative',
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'flex-start'
    },
    labelContainer: {
      backgroundColor: theme.paintscout.palette.brand.Neutral.P40,
      color: theme.palette.common.white,
      position: 'absolute',
      padding: `${theme.spacing(0.5)}px ${theme.spacing(1)}px`,
      borderRadius: theme.borderRadius.md,
      whiteSpace: 'nowrap',
      overflow: 'visible',
      display: 'inline-block'
    },
    barContainer: {
      position: 'relative',
      display: 'flex',
      flexDirection: 'row',
      width: '100%',
      grow: 1,
      height: theme.spacing(1.5),
      [theme.breakpoints.down('sm')]: {
        height: theme.spacing(1)
      }
    },
    stageIconBase: {
      position: 'absolute',
      top: -17,
      right: -30,
      zIndex: 1,
      [theme.breakpoints.down('sm')]: {
        right: -20
      }
    },
    icon: {
      fontSize: '2rem',
      [theme.breakpoints.down('sm')]: {
        fontSize: '1rem'
      }
    },
    progressBarBase: (props) => ({
      display: 'flex',
      grow: 1,
      width: '100%',
      '&:hover': props.disabled
        ? {}
        : {
            cursor: 'pointer'
          }
    }),
    progressBarFirstStage: {
      borderTopLeftRadius: theme.borderRadius.md,
      borderBottomLeftRadius: theme.borderRadius.md
    },
    progressBarLastStage: {
      borderTopRightRadius: theme.borderRadius.md,
      borderBottomRightRadius: theme.borderRadius.md
    },
    progressBarComplete: {
      backgroundColor: theme.paintscout.palette.primary
    },
    currentStage: {
      backgroundColor: theme.paintscout.palette.brand.Blue.Blue70
    },
    progressBarCompleteChevronAfter: (props) => ({
      '&:hover': props.disabled
        ? {}
        : {
            // backgroundColor: theme.paintscout.palette.dark,
            '&:after': {
              borderLeft: `6px solid ${theme.paintscout.palette.dark}`,
              [theme.breakpoints.down('sm')]: {
                borderLeft: `3px solid ${theme.paintscout.palette.dark}`
              }
            }
          },
      '&:after': {
        zIndex: 100,
        content: '""',
        position: 'absolute',
        left: '100%',
        borderTop: '11px solid transparent',
        borderBottom: '11px solid transparent',
        borderRight: '6px solid transparent',
        borderLeft: `6px solid ${theme.paintscout.palette.primary}`,
        [theme.breakpoints.down('sm')]: {
          borderTop: '4px solid transparent',
          borderBottom: '4px solid transparent',
          borderRight: '3px solid transparent',
          borderLeft: `3px solid ${theme.paintscout.palette.primary}`
        }
      }
    }),
    progressBarCompleteChevronBefore: (props) => ({
      // borderLeft: `1px solid ${theme.paintscout.palette.primary}`,
      '&:hover': props.disabled
        ? {}
        : {
            // backgroundColor: theme.paintscout.palette.dark,
            '&:before': {
              borderTop: `11px solid ${theme.paintscout.palette.dark}`,
              borderBottom: `11px solid ${theme.paintscout.palette.dark}`,
              [theme.breakpoints.down('sm')]: {
                borderTop: `4px solid ${theme.paintscout.palette.dark}`,
                borderBottom: `4px solid ${theme.paintscout.palette.dark}`
              }
            }
          },
      '&:before': {
        zIndex: 100,
        content: '""',
        position: 'absolute',
        right: '100%',
        borderTop: `11px solid ${theme.paintscout.palette.primary}`,
        borderBottom: `11px solid ${theme.paintscout.palette.primary}`,
        borderLeft: '6px solid transparent',
        [theme.breakpoints.down('sm')]: {
          borderTop: `4px solid ${theme.paintscout.palette.primary}`,
          borderBottom: `4px solid ${theme.paintscout.palette.primary}`,
          borderLeft: '3px solid transparent'
        }
      }
    }),
    progressBarIncomplete: (props) => ({
      backgroundColor: theme.paintscout.palette.brand.Neutral.P90,
      '&:hover': props.disabled
        ? {}
        : {
            // backgroundColor: theme.paintscout.palette.brand.Neutral.P80
          }
    }),
    progressBarIncompleteChevronAfter: (props) => ({
      '&:hover': props.disabled
        ? {}
        : {
            // backgroundColor: theme.paintscout.palette.brand.Neutral.P80,
            '&:after': {
              borderLeft: `6px solid ${theme.paintscout.palette.brand.Neutral.P80}`,
              [theme.breakpoints.down('sm')]: {
                borderLeft: `3px solid ${theme.paintscout.palette.brand.Neutral.P80}`
              }
            }
          },
      '&:after': {
        zIndex: 100,
        content: '""',
        position: 'absolute',
        left: '100%',
        borderTop: '11px solid transparent',
        borderBottom: '11px solid transparent',
        borderRight: '6px solid transparent',
        borderLeft: `6px solid ${theme.paintscout.palette.brand.Neutral.P90}`,
        [theme.breakpoints.down('sm')]: {
          borderTop: '4px solid transparent',
          borderBottom: '4px solid transparent',
          borderRight: '3px solid transparent',
          borderLeft: `3px solid ${theme.paintscout.palette.brand.Neutral.P90}`
        }
      }
    }),
    progressBarIncompleteChevronBefore: (props) => ({
      '&:hover': props.disabled
        ? {}
        : {
            // backgroundColor: theme.paintscout.palette.brand.Neutral.P80,
            '&:before': {
              borderTop: `11px solid ${theme.paintscout.palette.brand.Neutral.P80}`,
              borderBottom: `11px solid ${theme.paintscout.palette.brand.Neutral.P80}`,
              [theme.breakpoints.down('sm')]: {
                borderTop: `4px solid ${theme.paintscout.palette.brand.Neutral.P80}`,
                borderBottom: `4px solid ${theme.paintscout.palette.brand.Neutral.P80}`
              }
            }
          },
      '&:before': {
        zIndex: 100,
        content: '""',
        position: 'absolute',
        right: '100%',
        borderTop: `11px solid ${theme.paintscout.palette.brand.Neutral.P90}`,
        borderBottom: `11px solid ${theme.paintscout.palette.brand.Neutral.P90}`,
        borderLeft: '6px solid transparent',
        [theme.breakpoints.down('sm')]: {
          borderTop: `4px solid ${theme.paintscout.palette.brand.Neutral.P90}`,
          borderBottom: `4px solid ${theme.paintscout.palette.brand.Neutral.P90}`,
          borderLeft: '3px solid transparent'
        }
      }
    }),
    progressBarTime: {
      padding: theme.spacing(0.5)
    },
    // Dropdown styles
    pipelineSection: {
      borderBottom: `1px solid ${theme.palette.grey[300]}`,
      padding: theme.spacing(1)
    },
    pipelineOptions: {
      display: 'flex',
      gap: theme.spacing(1),
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1)
    },
    stageSection: {
      padding: theme.spacing(1)
    },
    stageOptions: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1),
      justifyContent: 'flex-start',
      alignItems: 'flex-start',
      paddingTop: theme.spacing(1)
    }
  }),
  { name: 'PipelineProgressBar' }
)

export interface PipelineProgressBarProps {
  classes?: StyleClasses<typeof useStyles>
  quoteView?: boolean
  pipeline: Pipeline
  deal?: Deal
  handleStageClick?: (updatedDeal: Deal) => void
  fixedStageId?: string
  showSelectOnMobile?: boolean
}

function PipelineProgressBar(props: PipelineProgressBarProps) {
  const classes = useStyles({ disabled: !props.handleStageClick, quoteView: props.quoteView })
  const { deal, pipeline, handleStageClick, quoteView, fixedStageId, showSelectOnMobile } = props
  const [previewedStageKey, setPreviewedStageKey] = useState<string | null>(null)
  const theme = useTheme<Theme>()
  const { options } = useClientOptions()
  const { productionFlag } = useFlags()
  const mobile = useIsMobile()

  // Use 'md' breakpoint (768px) to include iPad, or custom 1024px to include iPad Pro
  // This ensures touch devices without hover get the two-click interaction
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down('lg'), { noSsr: true })
  const [anchorEl, setAnchorEl] = useState(null)
  const selectRef = useRef<HTMLInputElement>(null)
  const [currentPipeline, setCurrentPipeline] = useState<Pipeline>(pipeline)
  const pipelines = getPipelines({ options })

  function handleStageBodyClick(stage: PipelineStage) {
    if (!handleStageClick) return

    if (deal?.stage?.value === stage.key) {
      return
    } else {
      const updatedDeal: Deal = {
        ...deal,
        stage: {
          value: stage.key
        }
      }
      handleStageClick(updatedDeal)
    }
  }

  // Auto-hide previewed stage after 3 seconds on mobile/tablet
  useEffect(() => {
    if (isMobileOrTablet && previewedStageKey) {
      const timer = setTimeout(() => {
        setPreviewedStageKey(null)
      }, 3000)

      return () => clearTimeout(timer)
    }
  }, [isMobileOrTablet, previewedStageKey])

  return (
    <div className={classes.container}>
      {!quoteView && (!mobile || showSelectOnMobile) && (
        <>
          <div
            className={classes.textContainer}
            onClick={(e) => {
              e.stopPropagation()
              if (!anchorEl) {
                setAnchorEl(e.currentTarget)
              } else {
                setAnchorEl(null)
              }
            }}
          >
            <Typography variant="body2" ref={selectRef}>
              {pipeline?.label} - {pipeline.stages.find((stage) => stage.key === deal?.stage?.value)?.label}
            </Typography>
            {anchorEl ? (
              <ExpandLessIcon className="block-click-away" />
            ) : (
              <ExpandMoreIcon className="block-click-away" />
            )}
          </div>
          <ClickAwayListener
            onClickAway={(e) => {
              const classList = (e.target as any)?.classList
              if (classList?.contains('block-click-away')) {
                return
              }

              setAnchorEl(null)
            }}
          >
            <Popper
              open={!!anchorEl}
              className={classes.popper}
              anchorEl={anchorEl}
              placement={'bottom-start'}
              disablePortal
            >
              <Paper
                id={'dropdown-menu-paper'}
                className={classes.paper}
                style={{ width: selectRef?.current?.clientWidth + (mobile ? 42 : 80) }}
              >
                {productionFlag && (
                  <div className={classes.pipelineSection}>
                    <Typography variant="subtitle2">Pipeline</Typography>
                    <div className={classes.pipelineOptions}>
                      <Button
                        variant="tab"
                        size="small"
                        active={currentPipeline.key === 'sales-pipeline'}
                        onClick={() => {
                          setCurrentPipeline(pipelines.find((p) => p.key === 'sales-pipeline')!)
                          // setAnchorEl(null)
                        }}
                      >
                        Sales
                      </Button>
                      <Button
                        variant="tab"
                        size="small"
                        active={currentPipeline.key !== 'sales-pipeline'}
                        onClick={() => {
                          setCurrentPipeline(pipelines.find((p) => p.key === 'production-pipeline')!)
                          // setAnchorEl(null)
                        }}
                      >
                        Production
                      </Button>
                    </div>
                  </div>
                )}
                <div className={classes.pipelineSection}>
                  {productionFlag && <Typography variant="subtitle1">Stage</Typography>}

                  <div className={classes.stageOptions}>
                    {currentPipeline.stages.map((stage) => (
                      <Button
                        key={stage.key}
                        variant="secondaryTab"
                        size="small"
                        active={deal?.stage?.value === stage.key && deal.pipeline.value === currentPipeline.key}
                        onClick={() => {
                          handleStageBodyClick(stage)
                          setAnchorEl(null)
                        }}
                      >
                        {stage.label.trim()}
                      </Button>
                    ))}
                  </div>
                </div>
              </Paper>
            </Popper>
          </ClickAwayListener>
        </>
      )}
      <div
        className={classNames(classes.pipelineContainer, 'tour-pipeline-progress-bar')}
        style={!productionFlag && mobile ? { marginBottom: '16px' } : {}}
      >
        {pipeline?.stages.map((stage, i) => {
          return (
            <PipelineStageBar
              disabled={!handleStageClick}
              quoteView={quoteView}
              handleStageBodyClick={handleStageBodyClick}
              deal={deal}
              pipeline={pipeline}
              stage={stage}
              fixedStageId={fixedStageId}
              i={i}
              key={i}
              previewedStageKey={previewedStageKey}
              setPreviewedStageKey={setPreviewedStageKey}
              isMobileOrTablet={isMobileOrTablet}
            />
          )
        })}
      </div>
    </div>
  )
}

function PipelineStageBar({
  i,
  deal,
  stage,
  pipeline,
  quoteView,
  handleStageBodyClick,
  fixedStageId,
  disabled,
  previewedStageKey,
  setPreviewedStageKey,
  isMobileOrTablet
}: {
  i: number
  deal: Deal
  stage: PipelineStage
  pipeline: Pipeline
  quoteView?: boolean
  handleStageBodyClick: (stage: PipelineStage) => void
  fixedStageId?: string
  disabled?: boolean
  previewedStageKey: string | null
  setPreviewedStageKey: (key: string | null) => void
  isMobileOrTablet: boolean
}) {
  const classes = useStyles({ disabled, quoteView })
  const [hovered, setHovered] = useState(false)
  const pipelineStages = pipeline?.stages ?? []
  const isLastStage = i === pipelineStages.length - 1
  const isFirstStage = i === 0
  const [stageHistory, setStageHistory] = useState<DealDocument['history']>(deal?.history ?? [])
  const currentStage = pipelineStages.findIndex((s) =>
    fixedStageId ? s.key === fixedStageId : s.key === deal?.stage?.value
  )
  const isCurrentStage = currentStage === i || (currentStage === -1 && i === 0)
  // When the deal's stage no longer exists in the pipeline (deleted stage), default visually to bar 0.
  // Look up history using the deal's actual stage key so the time shown is correct.
  const deletedStage = !fixedStageId && currentStage === -1
  const historyLookupKey = deletedStage && i === 0 ? deal?.stage?.value : stage.key
  const stageHistoryEntries = stageHistory.filter((h) => h.stage === historyLookupKey)
  // Use the last (most recent) history entry so re-entries don't show stale time from an old pass
  const dealStageHistory = stageHistoryEntries[stageHistoryEntries.length - 1]
  const completedStage = i < currentStage
  const isPreviewed = previewedStageKey === stage.key
  const { days, hours } = (() => {
    if ((!dealStageHistory?.enteredAt && !dealStageHistory?.exitedAt) || (!isCurrentStage && i > currentStage)) {
      return { days: 0, hours: 0 }
    }
    const enteredAt = new Date(dealStageHistory.enteredAt)
    const exitedAt = dealStageHistory.exitedAt ? new Date(dealStageHistory.exitedAt) : new Date()
    const timeDiff = exitedAt.getTime() - enteredAt.getTime()
    const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24))
    const hours = Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
    return { days, hours }
  })()

  useEffect(() => {
    setStageHistory(deal?.history ?? [])
  }, [deal])

  useEffect(() => {
    if (hovered) {
      const timer = setTimeout(() => {
        setHovered(false)
      }, 2000)

      return () => clearTimeout(timer)
    }
  }, [hovered])

  const handleClick = () => {
    if (disabled) return

    if (isMobileOrTablet) {
      // On mobile/iPad: two-click interaction
      if (isPreviewed) {
        // Second click: actually change the stage
        handleStageBodyClick(stage)
        setPreviewedStageKey(null)
      } else {
        // First click: show the stage name
        setPreviewedStageKey(stage.key)
      }
    } else {
      // On desktop: single click changes stage (hover shows tooltip)
      handleStageBodyClick(stage)
    }
  }

  // Show label on mobile/tablet when previewed, on desktop when hovered
  const showLabel = isMobileOrTablet ? isPreviewed : hovered
  const showTimeInStage = (isCurrentStage || completedStage) && (days > 0 || hours > 0)
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      key={stage.key}
      className={classes.stageContainer}
      onClick={handleClick}
    >
      <div
        className={classes.labelContainer}
        onMouseEnter={(ev) => ev.stopPropagation()}
        style={{
          opacity: showLabel ? 1 : 0,
          top: showTimeInStage ? -55 : showLabel ? -35 : 0,
          pointerEvents: showLabel ? 'auto' : 'none'
        }}
      >
        <Typography variant="subtitle1" color="secondary">
          {stage.label}
        </Typography>
        {showTimeInStage && (
          <Typography variant="caption" color="secondary" style={{ fontSize: '12px' }}>
            {days > 0 ? `${days} day${days === 1 ? '' : 's'}, ` : ''}
            {`${hours} hour${hours === 1 ? '' : 's'}`}
          </Typography>
        )}
      </div>
      <div className={classes.barContainer}>
        {(isCurrentStage || completedStage) && (
          <div
            className={classNames({
              [classes.progressBarBase]: true,
              [classes.progressBarComplete]: completedStage,
              [classes.currentStage]: isCurrentStage,
              [classes.progressBarFirstStage]: isFirstStage,
              [classes.progressBarLastStage]: isLastStage
            })}
          />
        )}
        {!completedStage && !isCurrentStage && (
          <div
            className={classNames({
              [classes.progressBarIncomplete]: true,
              // [classes.progressBarIncompleteChevronAfter]: !isLastStage,
              // [classes.progressBarIncompleteChevronBefore]: !isFirstStage,
              [classes.progressBarBase]: true,
              [classes.progressBarFirstStage]: isFirstStage,
              [classes.progressBarLastStage]: isLastStage
            })}
          />
        )}
      </div>
      {/* <div className={classes.progressBarTime}>
        {!isMobileOrTablet && !quoteView && (
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
            {(completedStage || isCurrentStage) && days > 0 && (
              <>
                <Typography variant="body2" color="textSecondary">
                  <strong>{days}</strong> Day{days === 1 ? '' : 's'}
                </Typography>
              </>
            )}
            {(isCurrentStage || hours > 0 || (days === 0 && hours === 0 && completedStage)) && days === 0 && (
              <div style={{ marginLeft: '5px' }}>
                <Typography variant="body2" color="textSecondary">
                  <strong>{hours}</strong> Hour{hours === 1 ? '' : 's'}
                </Typography>
              </div>
            )}
          </div>
        )}
      </div> */}
    </div>
  )
}

export default PipelineProgressBar
